
import { useState } from 'react';

export default function GovernmentPlatform() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  const platforms = [
    {
      id: 'guangdong-gov',
      title: '广东政务服务网',
      description: '省级统一政务服务平台，支持全省政务服务事项办理',
      icon: 'ri-government-line',
      url: 'www.gdzwfw.gov.cn',
      color: 'bg-blue-500',
      features: ['全省统一', '一网通办', '移动端支持', '实名认证'],
      services: ['企业开办', '个人事务', '投资项目', '不动产登记', '社会保障'],
      advantages: ['覆盖面广', '功能完善', '流程标准', '数据共享']
    },
    {
      id: 'guangzhou-gov',
      title: '广州市政务服务网',
      description: '广州市本级政务服务平台，提供市级部门服务事项',
      icon: 'ri-building-2-line',
      url: 'zwfw.gz.gov.cn',
      color: 'bg-green-500',
      features: ['市级服务', '本地特色', '部门齐全', '便民利企'],
      services: ['市场监管', '人社服务', '公安户政', '住建服务', '教育服务'],
      advantages: ['本地化强', '响应迅速', '服务精准', '办事便民']
    },
    {
      id: 'yueshenshi',
      title: '粤省事小程序',
      description: '广东省政务服务移动端平台，随时随地办事',
      icon: 'ri-smartphone-line',
      url: '微信搜索"粤省事"',
      color: 'bg-purple-500',
      features: ['移动办理', '扫码认证', '消息推送', '定位服务'],
      services: ['证照查询', '社保查询', '公积金', '交通违章', '健康码'],
      advantages: ['操作便捷', '功能丰富', '实时更新', '消息及时']
    },
    {
      id: 'enterprise-onestop',
      title: '广州市开办企业一网通',
      description: '企业开办专门平台，全流程网上办理',
      icon: 'ri-building-line',
      url: 'qygsone.gz.gov.cn',
      color: 'bg-orange-500',
      features: ['专业平台', '全程网办', '并联审批', '证照同步'],
      services: ['企业注册', '刻章备案', '银行开户', '税务报到', '社保登记'],
      advantages: ['流程优化', '时间压缩', '材料精简', '服务集成']
    }
  ];

  const commonFunctions = [
    {
      category: '用户注册登录',
      icon: 'ri-user-line',
      color: 'bg-blue-500',
      functions: [
        { name: '个人用户注册', description: '使用身份证进行实名认证注册' },
        { name: '企业用户注册', description: '使用统一社会信用代码注册企业账户' },
        { name: 'CA证书登录', description: '使用广东CA数字证书安全登录' },
        { name: '微信扫码登录', description: '通过微信扫码快速登录' }
      ]
    },
    {
      category: '事项查询办理',
      icon: 'ri-search-line',
      color: 'bg-green-500',
      functions: [
        { name: '服务事项搜索', description: '按关键词、部门、主题搜索办事事项' },
        { name: '办事指南查看', description: '详细了解办事条件、材料、流程' },
        { name: '在线申请办理', description: '填写申请表格，上传申请材料' },
        { name: '办理进度查询', description: '实时查看申请审批进度' }
      ]
    },
    {
      category: '材料上传管理',
      icon: 'ri-folder-upload-line',
      color: 'bg-purple-500',
      functions: [
        { name: '电子材料上传', description: '支持图片、PDF等格式文件上传' },
        { name: '材料模板下载', description: '下载标准申请表格和材料模板' },
        { name: '材料真实性承诺', description: '对上传材料进行真实性电子承诺' },
        { name: '补正材料提交', description: '根据审核意见补充完善材料' }
      ]
    },
    {
      category: '结果获取反馈',
      icon: 'ri-award-line',
      color: 'bg-orange-500',
      functions: [
        { name: '审批结果查询', description: '查看办理结果和审批决定' },
        { name: '电子证照下载', description: '下载电子版证书和证照' },
        { name: '快递配送选择', description: '选择EMS等快递服务配送纸质证照' },
        { name: '服务评价反馈', description: '对办事服务进行评价和建议' }
      ]
    }
  ];

  const stepByStepGuide = [
    {
      step: '1',
      title: '注册登录',
      description: '完成用户注册和实名认证',
      icon: 'ri-user-add-line',
      details: [
        '访问政务服务网首页，点击"注册"按钮',
        '选择用户类型（个人/企业），填写基本信息',
        '进行实名认证（身份证/营业执照）',
        '设置登录密码，完成注册流程'
      ],
      tips: [
        '个人用户建议使用身份证+手机号注册',
        '企业用户需要法定代表人实名认证',
        '首次登录建议完善个人资料'
      ]
    },
    {
      step: '2',
      title: '事项搜索',
      description: '查找需要办理的政务服务事项',
      icon: 'ri-search-2-line',
      details: [
        '在首页搜索框输入关键词',
        '可按部门、主题、服务对象分类查找',
        '点击事项名称查看详细办事指南',
        '确认办事条件和所需材料'
      ],
      tips: [
        '搜索时可使用事项全称或关键词',
        '注意区分省级、市级、区级事项',
        '仔细阅读办事指南避免材料不全'
      ]
    },
    {
      step: '3',
      title: '在线申请',
      description: '填写申请表格并上传材料',
      icon: 'ri-file-edit-line',
      details: [
        '点击"立即办理"进入申请页面',
        '按要求填写申请表格各项信息',
        '上传清晰完整的申请材料',
        '确认信息无误后提交申请'
      ],
      tips: [
        '信息填写要准确完整，避免审核退回',
        '材料扫描要清晰，文件大小不超过限制',
        '提交前仔细核对所有信息'
      ]
    },
    {
      step: '4',
      title: '进度跟踪',
      description: '查看办理进度和审核状态',
      icon: 'ri-time-line',
      details: [
        '登录后在"我的办事"中查看申请',
        '关注审核进度和状态变化',
        '及时查看审核意见和补正要求',
        '配合现场核查等环节'
      ],
      tips: [
        '定期登录查看办理进度',
        '及时处理补正材料要求',
        '保持手机畅通接收通知短信'
      ]
    },
    {
      step: '5',
      title: '结果获取',
      description: '获取办理结果和相关证照',
      icon: 'ri-award-fill',
      details: [
        '收到办结通知后登录查看结果',
        '下载电子证照或证明文件',
        '选择现场领取或快递配送',
        '对服务质量进行评价'
      ],
      tips: [
        '及时下载保存电子证照',
        '纸质证照选择安全的配送地址',
        '积极参与服务评价提升服务质量'
      ]
    }
  ];

  const commonProblems = [
    {
      category: '登录认证问题',
      icon: 'ri-key-line',
      color: 'text-red-600',
      problems: [
        {
          q: '忘记登录密码怎么办？',
          a: '点击登录页面"忘记密码"，通过注册手机号或邮箱重置密码。也可携带身份证到就近的政务服务中心现场重置。'
        },
        {
          q: '实名认证失败怎么处理？',
          a: '检查身份信息填写是否正确，确保身份证在有效期内。如持续失败，可能是系统数据更新延迟，建议稍后重试或联系客服。'
        },
        {
          q: 'CA证书无法使用？',
          a: '确认CA证书驱动已正确安装，证书在有效期内。建议使用IE或360浏览器，并允许运行ActiveX控件。'
        }
      ]
    },
    {
      category: '材料上传问题',
      icon: 'ri-upload-line',
      color: 'text-orange-600',
      problems: [
        {
          q: '上传文件提示格式不支持？',
          a: '检查文件格式是否为JPG、PNG、PDF等支持格式，文件大小不超过5MB。建议压缩后再上传。'
        },
        {
          q: '材料不清晰被退回？',
          a: '重新扫描或拍照，确保文字清晰可见，避免反光和阴影。建议使用扫描仪或专业扫描APP。'
        },
        {
          q: '上传后无法显示？',
          a: '可能是网络问题导致上传失败，建议刷新页面重新上传，或更换网络环境。'
        }
      ]
    },
    {
      category: '审核流程问题',
      icon: 'ri-file-search-line',
      color: 'text-blue-600',
      problems: [
        {
          q: '审核时间为什么比承诺时限长？',
          a: '承诺时限为工作日计算，不包含节假日。如材料需要补正，时间会重新计算。特殊情况可能需要现场核查。'
        },
        {
          q: '申请被退回如何处理？',
          a: '仔细查看退回原因，按照要求补充完善材料后重新提交。如有疑问可联系经办部门咨询。'
        },
        {
          q: '现场核查如何配合？',
          a: '接到核查通知后，按约定时间地点配合工作人员检查。准备好相关材料原件，确保联系方式畅通。'
        }
      ]
    }
  ];

  const securityTips = [
    {
      title: '账户安全',
      icon: 'ri-shield-user-line',
      tips: [
        '定期修改登录密码，使用复杂密码',
        '不在公共场所登录政务平台',
        '及时退出登录，不保存密码',
        '发现异常登录及时联系客服'
      ]
    },
    {
      title: '信息保护',
      icon: 'ri-file-shield-line',
      tips: [
        '不在非官方平台填写个人信息',
        '谨慎分享办事进度截图',
        '妥善保管电子证照文件',
        '定期清理浏览器缓存'
      ]
    },
    {
      title: '防范诈骗',
      icon: 'ri-alarm-warning-line',
      tips: [
        '政务服务不会主动要求转账',
        '警惕虚假政务网站和APP',
        '不点击可疑链接和附件',
        '有疑问及时向官方核实'
      ]
    }
  ];

  const detailedGuides = {
    'guangdong-gov': {
      title: '广东政务服务网使用指南',
      sections: [
        {
          title: '平台概述',
          icon: 'ri-information-line',
          content: [
            '【平台定位】广东政务服务网是全省统一的政务服务门户，整合了省、市、县三级政务服务资源',
            '【服务范围】涵盖企业开办、个人事务、投资项目、不动产登记、社会保障等各类政务服务',
            '【技术特色】支持PC端和移动端访问，提供统一身份认证、电子证照、电子签章等服务',
            '【用户规模】注册用户超过3000万，日均访问量超过200万次',
            '【服务优势】一网通办、全程网办、跨域通办、就近可办'
          ]
        },
        {
          title: '注册登录指南',
          icon: 'ri-user-settings-line',
          content: [
            '【个人用户注册】访问www.gdzwfw.gov.cn，点击右上角"注册"，选择"个人用户"',
            '【实名认证方式】支持身份证+手机号、微信实名、支付宝实名、银行卡实名等多种方式',
            '【企业用户注册】选择"法人用户"，使用统一社会信用代码和法定代表人信息注册',
            '【CA证书登录】企业用户建议申请广东CA数字证书，提供更高级别的安全保障',
            '【密码安全】设置8-20位包含字母、数字的复杂密码，定期更换',
            '【多端同步】一个账户可在PC端、手机端、粤省事小程序等多平台使用'
          ]
        },
        {
          title: '核心功能介绍',
          icon: 'ri-function-line',
          content: [
            '【智能搜索】支持关键词、部门、主题、服务对象等多维度搜索',
            '【分类导航】按"个人办事"、"企业办事"、"部门服务"、"便民服务"等分类',
            '【热门服务】首页展示最常用的政务服务事项，一键直达',
            '【我的办事】统一管理个人/企业的所有办事记录和进度',
            '【消息中心】接收审核通知、办结通知、政策推送等重要信息',
            '【帮助中心】提供详细的操作指南、常见问题解答和客服联系方式'
          ]
        },
        {
          title: '办事流程详解',
          icon: 'ri-flow-chart',
          content: [
            '第1步：搜索定位 - 通过搜索或分类找到需要办理的事项',
            '第2步：查看指南 - 点击事项名称，详细阅读办事指南，了解办理条件',
            '第3步：准备材料 - 按照材料清单准备所需证件和文件',
            '第4步：在线申请 - 点击"立即办理"，填写申请表格',
            '第5步：上传材料 - 按要求上传清晰的电子版材料',
            '第6步：提交审核 - 确认信息无误后提交申请',
            '第7步：进度跟踪 - 在"我的办事"中查看审核进度',
            '第8步：获取结果 - 办结后下载电子证照或选择邮寄服务'
          ]
        },
        {
          title: '特色服务功能',
          icon: 'ri-star-line',
          content: [
            '【秒批服务】部分高频事项实现系统自动审批，即时办结',
            '【容缺受理】允许次要材料后补，先受理先审查，提高办事效率',
            '【一件事一次办】将相关联的事项整合，一表申请、并联审批',
            '【跨域通办】省内异地可办，打破地域限制',
            '【电子证照库】汇聚各类电子证照，实现"免交"材料',
            '【信用承诺】部分事项可通过信用承诺方式减免材料'
          ]
        }
      ]
    },
    'guangzhou-gov': {
      title: '广州市政务服务网使用指南',
      sections: [
        {
          title: '平台特色',
          icon: 'ri-medal-line',
          content: [
            '【本地优势】专注广州市政务服务，更了解本地政策和流程',
            '【部门齐全】涵盖广州市所有政府部门和公共服务机构',
            '【响应迅速】本地化运营，问题处理和服务响应更及时',
            '【特色服务】结合广州特点，提供港澳台服务、外商投资等特色功能',
            '【多语支持】提供粤语、英语等多语言服务界面',
            '【线上线下融合】与广州市各级政务服务大厅紧密结合'
          ]
        },
        {
          title: '主要服务领域',
          icon: 'ri-service-line',
          content: [
            '【市场监管服务】企业登记注册、食品许可、特种设备等',
            '【人力资源社会保障】就业创业、社会保险、人才服务等',
            '【公安户政服务】户籍管理、居住证、出入境等',
            '【住房城乡建设】建筑许可、房屋交易、物业管理等',
            '【教育服务】入学申请、学历认证、教师资格等',
            '【卫生健康】医疗机构许可、公共卫生、计划生育等'
          ]
        },
        {
          title: '便民特色功能',
          icon: 'ri-heart-line',
          content: [
            '【预约服务】热门事项支持在线预约，避免现场排队',
            '【进度短信】关键节点自动发送短信通知',
            '【智能客服】7×24小时在线客服，快速解答常见问题',
            '【办事地图】显示就近的办事地点和交通路线',
            '【满意度评价】每笔业务完成后可进行服务评价',
            '【政策解读】及时发布最新政策解读和办事提醒'
          ]
        }
      ]
    },
    'yueshenshi': {
      title: '粤省事小程序使用指南',
      sections: [
        {
          title: '小程序优势',
          icon: 'ri-smartphone-line',
          content: [
            '【移动便捷】随时随地办事，无需安装APP',
            '【微信生态】与微信深度集成，操作更流畅',
            '【功能丰富】集成500+高频政务服务',
            '【实时推送】重要消息及时推送到微信',
            '【定位服务】自动定位提供就近服务',
            '【离线功能】部分功能支持离线使用'
          ]
        },
        {
          title: '核心服务功能',
          icon: 'ri-apps-line',
          content: [
            '【证照服务】身份证、驾驶证、行驶证等电子证照',
            '【社保查询】社保缴费记录、待遇查询、社保卡服务',
            '【公积金】账户查询、提取申请、贷款查询',
            '【交通出行】违章查询、路况信息、公交查询',
            '【健康医疗】健康码、疫苗接种、医保查询',
            '【教育服务】学历查询、考试成绩、入学申请'
          ]
        },
        {
          title: '使用技巧',
          icon: 'ri-lightbulb-line',
          content: [
            '【快速认证】首次使用时完成实名认证，后续免登录',
            '【收藏常用】将常用服务添加到"我的收藏"',
            '【消息通知】开启消息推送，及时接收办事通知',
            '【位置权限】授权位置信息，享受就近服务',
            '【分享功能】可将服务分享给家人朋友',
            '【意见反馈】遇到问题及时通过"意见反馈"渠道反映'
          ]
        }
      ]
    },
    'enterprise-onestop': {
      title: '广州市开办企业一网通使用指南',
      sections: [
        {
          title: '平台专业性',
          icon: 'ri-building-line',
          content: [
            '【专门设计】专为企业开办业务设计，流程更优化',
            '【部门协同】市场监管、公安、税务、银行等部门数据共享',
            '【并联审批】多部门同时审批，大幅压缩办理时间',
            '【标准统一】全市统一的企业开办标准和流程',
            '【服务集成】工商登记、刻章、银行开户、税务报到一站式办理',
            '【时限承诺】1-2个工作日内完成企业开办全流程'
          ]
        },
        {
          title: '开办流程优化',
          icon: 'ri-route-line',
          content: [
            '【信息共享】一次填报，多部门共享，减少重复录入',
            '【材料精简】通过数据共享减少材料提交',
            '【流程再造】原有串联流程改为并联流程',
            '【智能审批】符合条件的申请自动审批通过',
            '【集成服务】企业开办相关的所有环节一网办理',
            '【全程网办】从申请到领证全程网上办理'
          ]
        },
        {
          title: '增值服务',
          icon: 'ri-vip-line',
          content: [
            '【银行预约】可预约银行开户，享受绿色通道',
            '【刻章优惠】合作刻章企业提供优惠价格',
            '【税务辅导】提供税务政策咨询和辅导',
            '【后续服务】企业成立后的变更、注销等服务',
            '【政策推送】及时推送相关优惠政策信息',
            '【专家咨询】提供专业的企业服务咨询'
          ]
        }
      ]
    }
  };

  // 如果选择了详细指南，显示指南内容
  if (selectedGuide && detailedGuides[selectedGuide as keyof typeof detailedGuides]) {
    const guide = detailedGuides[selectedGuide as keyof typeof detailedGuides];
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSelectedGuide(null)}
                  className="text-indigo-600 hover:text-indigo-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{guide.title}</h1>
                  <p className="text-lg text-gray-600 mt-2">详细操作指南和使用技巧</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap">
                  收藏指南
                </button>
                <button className="border border-indigo-600 text-indigo-600 px-6 py-2 rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap">
                  打印指南
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Guide Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">指南目录</h3>
                <nav className="space-y-2">
                  {guide.sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="flex items-center p-3 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${section.icon} mr-3 text-indigo-600`}></i>
                      <span className="text-sm font-medium">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-8">
                {guide.sections.map((section, index) => (
                  <div
                    key={index}
                    id={`section-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${section.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">需要操作指导？</h3>
                  <p className="text-indigo-100 mb-6">我们提供政务平台使用的专业培训和指导服务</p>
                  <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      在线操作指导
                    </button>
                    <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
                      预约培训服务
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="text-indigo-600 hover:text-indigo-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">政务平台使用培训</h1>
                <p className="text-lg text-gray-600 mt-2">广东政务服务网、广州市政务服务网等平台操作指南</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-indigo-100 px-4 py-2 rounded-full">
                <span className="text-indigo-800 font-medium">学习进度: 0%</span>
              </div>
              <button className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/7fc2994d2ffe2961ac7b7bffd08641cd.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-indigo-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            政务服务平台操作指南
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
            掌握广东政务服务网、广州市政务服务网等主要政务平台的使用方法，提高办事效率
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">一网通办</div>
              <div className="text-indigo-100">全程网上办理</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">多端支持</div>
              <div className="text-indigo-100">PC端+移动端</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">操作简单</div>
              <div className="text-indigo-100">界面友好易用</div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">主要政务服务平台</h3>
            <p className="text-lg text-gray-600">了解各大政务服务平台的特点和适用场景</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {platforms.map((platform) => (
              <div key={platform.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`w-14 h-14 ${platform.color} rounded-lg flex items-center justify-center mr-4`}>
                      <i className={`${platform.icon} text-white text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{platform.title}</h4>
                      <p className="text-gray-600 text-sm">{platform.description}</p>
                      <p className="text-indigo-600 text-sm font-medium mt-1">{platform.url}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">平台特色</h5>
                    <div className="flex flex-wrap gap-2">
                      {platform.features.map((feature, index) => (
                        <span 
                          key={index}
                          className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">主要服务</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {platform.services.map((service, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2"></div>
                          {service}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-semibold text-gray-900 mb-2">核心优势</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {platform.advantages.map((advantage, index) => (
                        <div key={index} className="text-center">
                          <div className="bg-indigo-100 p-2 rounded-lg">
                            <span className="text-xs text-indigo-700 font-medium">{advantage}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedGuide(platform.id)}
                    className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看详细使用指南
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Functions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">通用功能操作</h3>
            <p className="text-lg text-gray-600">掌握政务平台的基础操作功能</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {commonFunctions.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`${category.color} p-4`}>
                  <div className="flex items-center justify-center text-white">
                    <i className={`${category.icon} text-2xl mr-3`}></i>
                    <h4 className="text-lg font-bold">{category.category}</h4>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {category.functions.map((func, funcIndex) => (
                      <div key={funcIndex} className="border-l-2 border-indigo-200 pl-4">
                        <h5 className="font-semibold text-gray-900 mb-1">{func.name}</h5>
                        <p className="text-gray-600 text-sm">{func.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step by Step Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">政务服务办理流程</h3>
            <p className="text-lg text-gray-600">标准化的五步办理流程，适用于大部分政务服务事项</p>
          </div>
          
          <div className="space-y-8">
            {stepByStepGuide.map((step, index) => (
              <div key={step.step} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
                  <div className="lg:col-span-1">
                    <div className="flex items-center mb-4">
                      <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mr-4">
                        <i className={`${step.icon} text-white text-2xl`}></i>
                      </div>
                      <div>
                        <div className="text-sm text-indigo-600 font-medium">第{step.step}步</div>
                        <h4 className="text-xl font-bold text-gray-900">{step.title}</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">{step.description}</p>
                  </div>
                  
                  <div className="lg:col-span-1">
                    <h5 className="font-semibold text-gray-900 mb-3">操作步骤：</h5>
                    <div className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-start">
                          <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <span className="text-indigo-600 font-bold text-xs">{detailIndex + 1}</span>
                          </div>
                          <span className="text-gray-700 text-sm">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="lg:col-span-1">
                    <h5 className="font-semibold text-gray-900 mb-3">注意事项：</h5>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <div className="space-y-2">
                        {step.tips.map((tip, tipIndex) => (
                          <div key={tipIndex} className="flex items-start">
                            <i className="ri-lightbulb-line text-amber-600 mr-2 mt-0.5"></i>
                            <span className="text-amber-800 text-sm">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Problems */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">常见问题解答</h3>
            <p className="text-lg text-gray-600">解决使用政务平台过程中的常见问题</p>
          </div>
          
          <div className="space-y-8">
            {commonProblems.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${category.icon} text-white text-xl`}></i>
                    </div>
                    <h4 className="text-xl font-bold text-white">{category.category}</h4>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    {category.problems.map((problem, problemIndex) => (
                      <div key={problemIndex} className="border-l-4 border-indigo-200 pl-6">
                        <div className="flex items-center mb-2">
                          <i className="ri-question-line text-indigo-600 mr-2"></i>
                          <h5 className="font-semibold text-gray-900">{problem.q}</h5>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{problem.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Tips */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">安全使用提醒</h3>
            <p className="text-lg text-gray-600">保护个人信息安全，安全使用政务服务平台</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {securityTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg border-l-4 border-red-500 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${tip.icon} text-red-600 text-xl`}></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">{tip.title}</h4>
                  </div>
                  <div className="space-y-3">
                    {tip.tips.map((tipItem, tipIndex) => (
                      <div key={tipIndex} className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{tipItem}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <i className="ri-alarm-warning-line text-red-600 text-xl mr-2"></i>
              <h4 className="text-lg font-semibold text-red-800">重要提醒</h4>
            </div>
            <p className="text-red-700">
              政务服务平台承载着大量个人敏感信息，使用时务必提高安全意识。
              如遇到可疑情况或发现账户异常，请立即联系相关部门客服进行处理。
              切记：任何政务服务都不会要求您转账汇款！
            </p>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">获得帮助支持</h3>
            <p className="text-xl text-indigo-100">多种渠道为您提供政务平台使用指导</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">在线客服</h4>
              <p className="text-indigo-100 text-sm">7×24小时在线服务</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">服务热线</h4>
              <p className="text-indigo-100 text-sm">12345政务服务热线</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">现场服务</h4>
              <p className="text-indigo-100 text-sm">政务服务中心现场指导</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-open-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">操作手册</h4>
              <p className="text-indigo-100 text-sm">详细的图文操作指南</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            掌握政务平台操作，提高办事效率
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            专业的培训指导，让您快速成为政务服务平台操作专家
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-indigo-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap">
              开始学习培训
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              获取操作手册
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">联系我们</h4>
              <div className="space-y-2 text-gray-300">
                <p>电话: 020-1234-5678</p>
                <p>邮箱: platform@example.com</p>
                <p>地址: 广州市天河区xxx路123号</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">快速链接</h4>
              <div className="space-y-2 text-gray-300">
                <p className="cursor-pointer hover:text-white" onClick={() => window.REACT_APP_NAVIGATE('/')}>返回首页</p>
                <p>平台指南</p>
                <p>操作培训</p>
                <p>常见问题</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">政务平台</h4>
              <div className="space-y-2 text-gray-300">
                <p>广东政务服务网</p>
                <p>广州市政务服务网</p>
                <p>粤省事小程序</p>
                <p>开办企业一网通</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">培训服务</h4>
              <div className="space-y-2 text-gray-300">
                <p>平台操作指导</p>
                <p>实操演练培训</p>
                <p>问题解答服务</p>
                <p><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Made with Readdy</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 企服培训平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
